import { useAuth0 } from '@auth0/auth0-react'
import { Card } from '@helix-ds/react'
import * as Sentry from '@sentry/react'
import React, { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, Navigate, useLocation } from 'react-router-dom'

/** Remove first-login keys written by PR #76 before server-side check-first-login. */
function clearLegacyFirstLoginStorage(userSub?: string | null): void {
  try {
    localStorage.removeItem('familyPortal.hasLoggedIn')
    sessionStorage.removeItem('familyPortal.freshLogin')
    if (userSub) {
      localStorage.removeItem(`familyPortal.hasLoggedIn.${userSub}`)
      sessionStorage.removeItem(`familyPortal.freshLogin.${userSub}`)
    }
  } catch {
    // ignore private browsing / quota errors
  }
}

const LoginCallback: React.FC = () => {
  const intl = useIntl()
  const { isAuthenticated, isLoading, error, user } = useAuth0()
  const location = useLocation()

  useEffect(() => {
    if (isAuthenticated) {
      clearLegacyFirstLoginStorage(user?.sub)
    }
  }, [isAuthenticated, user?.sub])

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        tags: {
          operation: 'auth.callback',
        },
        extra: {
          path: location.pathname,
          search: location.search,
        },
      })
    }
  }, [error, location.pathname, location.search])

  if (isAuthenticated) {
    return <Navigate to="/home" replace />
  }

  if (error) {
    const isMagicLinkError =
      /no email or no verification_code provided/i.test(error.message) ||
      /access_denied/i.test(String((error as { error?: string }).error))

    return (
      <div className="bg-bg-secondary flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <h1 className="text-text-heading text-lg font-semibold">
            <FormattedMessage id="loginCallback.error.heading" defaultMessage="Sign-in failed" />
          </h1>
          <p className="text-text-body mt-2 text-sm">{error.message}</p>
          {isMagicLinkError && (
            <>
              <p className="text-text-subtle mt-3 text-sm">
                <FormattedMessage
                  id="loginCallback.error.magicLinkHint"
                  defaultMessage="If you used the email link: open the link in the <strong>same browser</strong> (and tab) where you started sign-in. Otherwise try again and enter the code from the email."
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              </p>
              <p className="text-text-subtle mt-2 text-sm">
                <FormattedMessage
                  id="loginCallback.error.tenantHint"
                  defaultMessage='If you never saw Auth0&apos;s sign-in form, your tenant may need: Authentication Profile = "Identifier First", Passwordless Email connection enabled for this app, and Allowed Callback URLs including this origin.'
                />
              </p>
            </>
          )}
          <div className="mt-6">
            <Link
              to="/login"
              className="bg-brand-600 hover:bg-brand-700 focus:ring-brand-600/20 inline-flex items-center justify-center rounded-full px-4 py-2.5 text-sm font-medium text-white transition-colors focus:ring-4 focus:outline-none"
            >
              <FormattedMessage id="loginCallback.error.returnToLogin" defaultMessage="Return to login" />
            </Link>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="bg-bg-secondary flex min-h-screen items-center justify-center">
      <Card>
        <div className="text-text-body text-sm">
          {isLoading
            ? intl.formatMessage({
                id: 'loginCallback.loading.completingSignIn',
                defaultMessage: 'Completing sign-in…',
              })
            : intl.formatMessage({ id: 'loginCallback.loading.continuing', defaultMessage: 'Continuing…' })}
        </div>
      </Card>
    </div>
  )
}

export default LoginCallback
